import React, { useState } from 'react';
import * as R from 'ramda';
import { useListContext } from 'react-admin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { green, orange, red, grey } from '@material-ui/core/colors';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import moment from 'moment';

import { FullNameField } from 'resources/students/fields';
import { OfflineList } from 'resources/common';
import QuizSubmissionDetail from './QuizSubmissionDetail';

const getStudentLinkId = (record) => {
  if (record.studentId) return record.studentId;
  if (record.student_id) return record.student_id;
  // Pending rows use `id` as the student id; submissions use `id` as quiz id.
  if (!record.submissionId) return record.id;
  return null;
};

const useStyles = makeStyles({
  score: {
    fontWeight: 'bold',
  },
  highScore: {
    color: green[700],
  },
  mediumScore: {
    color: orange[700],
  },
  lowScore: {
    color: red[700],
  },
  expandCell: {
    width: 48,
    paddingLeft: 8,
    paddingRight: 0,
  },
  row: {
    cursor: 'pointer',
  },
  rowDisabled: {
    cursor: 'default',
  },
  detailCell: {
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: '#fafafa',
  },
  studentLink: {
    color: '#1565c0',
    cursor: 'pointer',
    display: 'inline-flex',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const rowStyle = (record) => {
  if (!record.submissionId) {
    return {
      borderLeftColor: grey[500],
      borderLeftWidth: 5,
      borderLeftStyle: 'solid',
    };
  }

  const score = R.propOr(0, 'globalScore', record);
  if (score >= 70) {
    return {
      borderLeftColor: green[500],
      borderLeftWidth: 5,
      borderLeftStyle: 'solid',
    };
  }
  if (score >= 40) {
    return {
      borderLeftColor: orange[500],
      borderLeftWidth: 5,
      borderLeftStyle: 'solid',
    };
  }
  return {
    borderLeftColor: red[500],
    borderLeftWidth: 5,
    borderLeftStyle: 'solid',
  };
};

const ScoreField = ({ record }) => {
  const classes = useStyles();
  const score = R.propOr(null, 'globalScore', record);

  if (score === null) return <span>Pending</span>;

  let scoreClass = classes.score;
  if (score >= 70) {
    scoreClass = `${classes.score} ${classes.highScore}`;
  } else if (score >= 40) {
    scoreClass = `${classes.score} ${classes.mediumScore}`;
  } else {
    scoreClass = `${classes.score} ${classes.lowScore}`;
  }

  return <span className={scoreClass}>{score}%</span>;
};

const SubmissionDateField = ({ record }) => {
  const submissionDate = record.submittedAt || record.created_at;
  if (!submissionDate) return <span>—</span>;
  return <span>{moment(submissionDate).format('DD/MM/YYYY, h:mm A')}</span>;
};

const getStudentKey = (student) =>
  student.email || student.submissionId || student.id;

const SubmissionsTable = ({ quiz, course }) => {
  const classes = useStyles();
  const { data, ids, currentSort, setSort } = useListContext();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const progressTab = course ? `${course}_progress` : 'course_progress';

  const handleSort = (field) => {
    const order =
      currentSort.field === field && currentSort.order === 'ASC' ? 'DESC' : 'ASC';
    setSort(field, order);
  };

  const toggleStudent = (studentKey, canExpand) => {
    if (!canExpand) return;
    setExpandedKeys((current) =>
      current.includes(studentKey)
        ? current.filter((key) => key !== studentKey)
        : [...current, studentKey]
    );
  };

  const openStudentQuizzes = (event, record) => {
    event.preventDefault();
    event.stopPropagation();
    const studentId = getStudentLinkId(record);
    if (!studentId) return;
    window.open(
      `/#/students/${studentId}/show/${progressTab}/quizzes`,
      '_blank'
    );
  };

  const renderSortLabel = (field, label) => (
    <TableSortLabel
      active={currentSort.field === field}
      direction={
        currentSort.field === field
          ? currentSort.order.toLowerCase()
          : 'asc'
      }
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell className={classes.expandCell} />
          <TableCell>
            {renderSortLabel('first_name', 'Full Name')}
          </TableCell>
          <TableCell>{renderSortLabel('email', 'Email')}</TableCell>
          <TableCell>{renderSortLabel('globalScore', 'Score')}</TableCell>
          <TableCell>Submission Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {ids.map((id) => {
          const record = data[id];
          if (!record) return null;
          const studentKey = getStudentKey(record);
          const canExpand = Boolean(record.submissionId);
          const isExpanded = expandedKeys.includes(studentKey);
          const studentLinkId = getStudentLinkId(record);

          return (
            <React.Fragment key={studentKey}>
              <TableRow
                hover={canExpand}
                className={canExpand ? classes.row : classes.rowDisabled}
                style={rowStyle(record)}
                onClick={() => toggleStudent(studentKey, canExpand)}
              >
                <TableCell className={classes.expandCell}>
                  {canExpand
                    ? isExpanded
                      ? <KeyboardArrowUp />
                      : <KeyboardArrowDown />
                    : null}
                </TableCell>
                <TableCell>
                  {studentLinkId ? (
                    <span
                      role="link"
                      tabIndex={0}
                      className={classes.studentLink}
                      onClick={(event) => openStudentQuizzes(event, record)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          openStudentQuizzes(event, record);
                        }
                      }}
                    >
                      <FullNameField record={record} />
                    </span>
                  ) : (
                    <FullNameField record={record} />
                  )}
                </TableCell>
                <TableCell>{record.email}</TableCell>
                <TableCell>
                  <ScoreField record={record} />
                </TableCell>
                <TableCell>
                  <SubmissionDateField record={record} />
                </TableCell>
              </TableRow>
              {isExpanded && canExpand && (
                <TableRow>
                  <TableCell className={classes.detailCell} colSpan={5}>
                    <QuizSubmissionDetail record={record} quiz={quiz} />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

const QuizSubmissionsList = ({ record: quiz, course }) => {
  const submissions = R.propOr([], 'submissions', quiz);
  const pendingStudents = R.pipe(
    R.propOr([], 'pending'),
    R.map((student) => ({
      ...student,
      submissionId: null,
      globalScore: null,
    }))
  )(quiz);

  const allStudents = R.map(
    (student) => ({
      ...student,
      _rowKey:
        student.submissionId ||
        student.studentId ||
        student.student_id ||
        student.email ||
        student.id,
    }),
    [...submissions, ...pendingStudents]
  );

  return (
    <OfflineList
      list={allStudents}
      uniqueKey="_rowKey"
      defaultSort={{ field: 'globalScore', order: 'DESC' }}
      sorters={{
        globalScore: R.prop('globalScore'),
        first_name: R.propOr('', 'first_name'),
        email: R.propOr('', 'email'),
      }}
      nullsLastFields={['globalScore']}
    >
      <SubmissionsTable quiz={quiz} course={course} />
    </OfflineList>
  );
};

export default QuizSubmissionsList;
